import React, { useRef, useState } from 'react';
import { cloneJsonValue } from '../../utils/derivedRunDraft';

const AUTOMATIC_STRATEGIES = [
  ['adaptive', 'Adaptive'], ['symmetric_area_capped', 'Symmetric area capped'],
];
const BASELINES = [
  ['shared', 'Shared constant'], ['shared_linear', 'Shared linear'],
  ['per_component', 'Constant per component'],
];
const inputClass = 'w-full rounded border border-slate-300 p-2 text-sm';

function NumericControl({ label, value, onChange, disabled, precision, ...options }) {
  const [focused, setFocused] = useState(false);
  const displayed = !focused && precision != null && typeof value === 'number' && Number.isFinite(value)
    ? Number(value.toFixed(precision)) : value ?? '';
  return <label className="block"><span className="mb-1 block text-xs font-medium text-slate-700">{label}</span>
    <input type="number" step="any" {...options} className={inputClass} value={displayed} disabled={disabled}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      onChange={(event) => onChange(event.target.value === '' ? '' : Number(event.target.value))} />
  </label>;
}

export function getSizeDraftError(value) {
  const region = value?.region || {};
  const seeds = value?.fitter?.shared_t0_sigma_seeds_s;
  const lower = value?.fitter?.baseline_lo === undefined ? 0 : value.fitter.baseline_lo;
  const upper = value?.fitter?.baseline_hi === undefined ? 1e9 : value.fitter.baseline_hi;
  if (typeof lower !== 'number' || !Number.isFinite(lower) ||
      typeof upper !== 'number' || !Number.isFinite(upper)) return 'Baseline bounds must be finite numbers.';
  if (lower >= upper) return 'Baseline lower bound must be less than upper bound.';
  if (upper < 0 || (value?.fitter?.model === 'per_component' && lower > 0)) {
    return value?.fitter?.model === 'per_component'
      ? 'Baseline bounds must include the initial offset of 0 for the per-component model.'
      : 'Baseline bounds exclude the initial offset: shared models start at zero or a positive value.';
  }

  const finite = (v) => typeof v === 'number' && Number.isFinite(v);
  if (region.strategy === 'declared_window' &&
      (!finite(region.window_start_s) || !finite(region.window_end_s) ||
       region.window_start_s >= region.window_end_s)) {
    return 'Enter a finite Start time before End time.';
  }
  if (seeds != null && (!Array.isArray(seeds) || seeds.length < 1 || seeds.length > 4 ||
      seeds.some((v) => !finite(v) || v <= 0))) {
    return 'Enter a positive sigma seed for each of 1–4 components.';
  }
  return null;
}

export function DerivedSizeParamsEditor({ value, onChange, disabled, sourceParams }) {
  const region = value?.region || {};
  const fitter = value?.fitter || {};
  const smoothing = value?.smoothing || {};
  const manual = region.strategy === 'declared_window';
  const seeds = fitter.shared_t0_sigma_seeds_s;
  const shared = seeds != null;
  const rememberedSeeds = useRef(seeds || ['']);
  if (Array.isArray(seeds)) rememberedSeeds.current = seeds;
  const update = (section, changes) => {
    const next = cloneJsonValue(value || {});
    next[section] = { ...next[section], ...changes };
    onChange(next);
  };
  const number = (section, key, label, options = {}) => (
    <NumericControl key={`${section}-${key}`} label={label} value={value?.[section]?.[key]}
      disabled={disabled} {...options} onChange={(next) => update(section, { [key]: next })} />
  );
  const editWindow = (key, nextValue) => update('region', { strategy: 'declared_window', [key]: nextValue });
  const resetRegion = () => {
    const next = cloneJsonValue(value);
    next.region = { ...next.region };
    for (const key of ['strategy', 'window_start_s', 'window_end_s']) {
      if (Object.prototype.hasOwnProperty.call(sourceParams?.region || {}, key)) next.region[key] = sourceParams.region[key];
      else delete next.region[key];
    }
    onChange(next);
  };
  const toggleShared = (enabled) => {
    const next = cloneJsonValue(value);
    next.fitter = { ...next.fitter };
    if (enabled) next.fitter.shared_t0_sigma_seeds_s = cloneJsonValue(rememberedSeeds.current);
    else if (sourceParams?.fitter?.shared_t0_sigma_seeds_s != null) next.fitter.shared_t0_sigma_seeds_s = null;
    else if (Object.prototype.hasOwnProperty.call(sourceParams?.fitter || {}, 'shared_t0_sigma_seeds_s')) next.fitter.shared_t0_sigma_seeds_s = sourceParams.fitter.shared_t0_sigma_seeds_s;
    else delete next.fitter.shared_t0_sigma_seeds_s;
    onChange(next);
  };
  const choice = (label, current, options, change) => (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{label}</span>
      <select value={current ?? ''} onChange={(e) => change(e.target.value)} disabled={disabled} className={inputClass}>
        {!options.some(([key]) => key === current) && <option value={current ?? ''} disabled>{current ? `Inherited: ${current}` : 'Inherited default'}</option>}
        {options.map(([key, name]) => <option key={key} value={key}>{name}</option>)}
      </select>
    </label>
  );
  return (
    <div className="space-y-4">
      <section className="space-y-2" aria-label="Fitting region">
        <div className="flex items-center justify-between gap-2"><h4 className="text-sm font-semibold">Fitting region</h4>
          <button type="button" disabled={disabled} onClick={resetRegion} className="text-xs text-slate-600 underline disabled:opacity-50">Reset region</button></div>
        <div className="grid grid-cols-2 gap-2">
          <NumericControl label="Start (s)" value={region.window_start_s} precision={3} disabled={disabled} onChange={(v) => editWindow('window_start_s', v)} />
          <NumericControl label="End (s)" value={region.window_end_s} precision={3} disabled={disabled} onChange={(v) => editWindow('window_end_s', v)} />
        </div>
        <p className="text-xs text-slate-500">You can also select the fitting region directly on the Taylorgram by clicking and dragging.</p>
        <p className="text-xs text-slate-500">{manual ? 'Declared window • validated on preview.' : 'Inherited automatic region selection remains active until you edit a boundary or drag.'}</p>
      </section>
      <section className="border-t pt-3 space-y-2" aria-label="Fitting model and parameters">
        <h4 className="text-sm font-semibold">Fitting model</h4>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={shared} disabled={disabled}
          onChange={(e) => toggleShared(e.target.checked)} />Shared t0 multi-Gaussian fitting</label>
        {shared && <>
          {choice('Gaussian components', String(Array.isArray(seeds) ? seeds.length : 1),
            [1, 2, 3, 4].map((n) => [String(n), String(n)]),
            (count) => update('fitter', { shared_t0_sigma_seeds_s: Array.from({ length: Number(count) }, (_, i) => seeds?.[i] ?? '') }))}
          <div className="grid gap-3 md:grid-cols-2">{(Array.isArray(seeds) ? seeds : []).map((seed, i) => (
            <label key={i} className="block">Sigma seed {i + 1} (s)
              <input type="number" step="any" min="0" className={inputClass} value={seed} disabled={disabled}
                onChange={(e) => update('fitter', { shared_t0_sigma_seeds_s: seeds.map((v, j) => i === j ? (e.target.value === '' ? '' : Number(e.target.value)) : v) })} />
            </label>
          ))}</div>
          <p className="text-sm text-slate-500">Initial width estimates; widths and the common t0 are fitted. Component count stays fixed.</p>
        </>}
      </section>
      <section className="border-t pt-3 space-y-2" aria-label="Basic settings">
        <h4 className="text-sm font-semibold">Basic settings</h4>
        {choice('Baseline model', fitter.model, BASELINES, (model) => update('fitter', { model }))}
        {number('detection', 'prominence', 'Prominence', { min: 0 })}
        <p className="text-xs text-slate-500">Prominence uses the analysis signal units shown with the source trace.</p>
        <label className="flex gap-2 items-center"><input type="checkbox" checked={Boolean(smoothing.enabled)}
          disabled={disabled} onChange={(e) => update('smoothing', { enabled: e.target.checked })} />Smoothing</label>
      </section>
      <details className="border-t pt-3">
        <summary className="cursor-pointer font-semibold">Advanced settings</summary>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {!manual && <h5 className="col-span-2 text-xs font-semibold text-slate-500">Automatic region</h5>}
          {!manual && choice('Automatic region strategy', region.strategy, AUTOMATIC_STRATEGIES, (strategy) => update('region', { strategy }))}
          {!manual && region.strategy === 'symmetric_area_capped' && <>
            {number('region', 'max_factor', 'Maximum width factor')}
            {number('region', 'step', 'Region growth step')}
            {number('region', 'gain_thresh', 'Minimum area gain')}
          </>}
          {!manual && region.strategy === 'adaptive' && <>
            {number('region', 'guard_valley_frac', 'Valley guard fraction')}
            <label><input type="checkbox" disabled={disabled} checked={region.knee_drop != null}
              onChange={(e) => update('region', { knee_drop: e.target.checked ? '' : null })} /> Use knee stopping</label>
            {region.knee_drop != null && number('region', 'knee_drop', 'Knee drop')}
          </>}
          <h5 className="col-span-2 mt-2 text-xs font-semibold text-slate-500">Peak detection</h5>
          {number('detection', 'area_threshold', 'Minimum relative peak area')}
          {number('detection', 'min_width_seconds', 'Minimum peak width (s)')}
          {number('detection', 'min_distance_seconds', 'Minimum peak spacing (s)')}
          <h5 className="col-span-2 mt-2 text-xs font-semibold text-slate-500">Fit bounds and convergence</h5>
          {number('fitter', 'min_sigma', 'Minimum fitted sigma (s)')}
          {number('fitter', 'baseline_lo', 'Baseline lower bound')}
          {number('fitter', 'baseline_hi', 'Baseline upper bound')}
          <p className="col-span-2 text-xs text-slate-500">Bounds use analysis signal units. Preview checks the initial offset against the selected trace and fitting region.</p>
          {!shared && <>
            {number('fitter', 'max_attempts', 'Maximum fitting attempts', { step: 1, min: 1 })}
            {number('fitter', 'r2_threshold', 'Target R²')}
            {number('fitter', 'rmse_threshold', 'Target RMSE')}
            {number('fitter', 'min_peak_distance', 'Residual peak spacing (s)')}
          </>}
          {smoothing.enabled && <>
            <h5 className="col-span-2 mt-2 text-xs font-semibold text-slate-500">Smoothing</h5>
            {number('smoothing', 'window', 'Smoothing window (samples)', { step: 2, min: 3 })}
            {number('smoothing', 'poly', 'Smoothing polynomial order', { step: 1, min: 0 })}
            {number('smoothing', 'deriv', 'Smoothing derivative order', { step: 1, min: 0 })}
            {number('smoothing', 'delta', 'Smoothing sample spacing')}
            {choice('Smoothing edge mode', smoothing.mode, ['interp', 'mirror', 'constant', 'nearest', 'wrap'].map((m) => [m, m]), (mode) => update('smoothing', { mode }))}
          </>}
        </div>
      </details>
    </div>
  );
}
